/** XeAgents.ai - SEO Dashboard **/
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Search, Globe, FileText, BarChart3, Link2, RefreshCw,
  Plus, Play, Pause, Clock, CheckCircle2, XCircle, AlertTriangle,
  Sparkles, TrendingUp, TrendingDown, Minus, Eye, Share2,
  Code, Wand2, Loader2, ExternalLink, Linkedin, Download, FileDown
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { formatRelativeTime } from "@/lib/date-format";

interface DashboardStats {
  publishedBlogPosts: number;
  publishedPages: number;
  publicTemplates: number;
  activeWidgets: number;
  activeBacklinks: number;
  trackedKeywords: number;
}

interface CronJob {
  id: string;
  jobType: string;
  jobName: string;
  cronExpression: string | null;
  isEnabled: boolean;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  nextRunAt: string | null;
  config: Record<string, any>;
}

interface AuditLog {
  id: string;
  action: string;
  category: string;
  status: string;
  message: string;
  createdAt: string;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  status: string;
  category: string;
  viewCount: number;
  publishedAt: string | null;
  aiGenerated: boolean;
}

interface Keyword {
  id: string;
  keyword: string;
  currentPosition: number | null;
  previousPosition: number | null;
  targetUrl: string | null;
  lastCheckedAt: string | null;
}

// Helper component for automation action cards
function AutomationCard({
  title,
  description,
  endpoint,
  icon,
}: {
  title: string;
  description: string;
  endpoint: string;
  icon: React.ReactNode;
}) {
  const [isRunning, setIsRunning] = useState(false);
  const { toast } = useToast();

  const handleRun = async () => {
    setIsRunning(true);
    try {
      const res = await apiRequest("POST", endpoint);
      const data = await res.json();
      toast({
        title: `${title} completed`,
        description: data.message || "Task executed successfully",
      });
    } catch (error) {
      toast({
        title: `${title} failed`,
        description: "An error occurred while running the task",
        variant: "destructive",
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          </div>
        </div>
        <Button
          className="w-full mt-4"
          variant="outline"
          onClick={handleRun}
          disabled={isRunning}
        >
          {isRunning ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Run Now
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// Helper component for schedule display items
function ScheduleItem({ task, schedule }: { task: string; schedule: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="flex items-center gap-3">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{task}</span>
      </div>
      <Badge variant="outline">{schedule}</Badge>
    </div>
  );
}

const CRON_DAY_NAMES = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];

/**
 * Plain-English label for the common shapes the SEO jobs use. Falls back to the
 * raw expression rather than inventing a description it cannot back up.
 */
function describeCron(expression: string | null): string {
  if (!expression) return "Not scheduled";

  const fields = expression.trim().split(/\s+/);
  if (fields.length !== 5) return expression;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;
  if (dayOfMonth !== "*" || month !== "*") return expression;

  const time = (h: string, m: string) => `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;

  if (minute.startsWith("*/") && hour === "*") {
    return `Every ${minute.slice(2)} minutes`;
  }
  if (hour.startsWith("*/") && /^\d+$/.test(minute)) {
    const offset = Number(minute) > 0 ? ` (${minute} min past)` : "";
    return `Every ${hour.slice(2)} hours${offset}`;
  }
  if (minute === "0" && hour === "*") {
    return "Every hour";
  }
  if (!/^\d+$/.test(minute) || !/^\d+$/.test(hour)) {
    return expression;
  }
  if (dayOfWeek === "*") {
    return `Daily at ${time(hour, minute)}`;
  }
  if (/^\d$/.test(dayOfWeek)) {
    return `${CRON_DAY_NAMES[Number(dayOfWeek) % 7]} at ${time(hour, minute)}`;
  }

  return expression;
}

export default function SEODashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [isGeneratingBlog, setIsGeneratingBlog] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [blogTopic, setBlogTopic] = useState("");
  const [blogKeywords, setBlogKeywords] = useState("");
  const [newKeyword, setNewKeyword] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch dashboard data
  const { data: dashboard, isLoading: dashboardLoading } = useQuery<{
    stats: DashboardStats;
    recentLogs: AuditLog[];
    cronStatus: any;
  }>({
    queryKey: ["/api/seo/dashboard"],
  });

  // Fetch cron jobs
  const { data: cronJobs } = useQuery<CronJob[]>({
    queryKey: ["/api/seo/cron/jobs"],
  });

  // Fetch blog posts
  const { data: blogPosts } = useQuery<BlogPost[]>({
    queryKey: ["/api/seo/blog"],
  });

  // Fetch keywords
  const { data: keywords } = useQuery<Keyword[]>({
    queryKey: ["/api/seo/keywords"],
  });

  // Mutations
  const refreshSitemapMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/seo/sitemap/refresh");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Sitemap refreshed successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/seo/dashboard"] });
    },
    onError: () => {
      toast({ title: "Failed to refresh sitemap", variant: "destructive" });
    },
  });

  const pingSearchEnginesMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/seo/sitemap/ping");
      return res.json();
    },
    onSuccess: (data) => {
      const successCount = data.results?.filter((r: any) => r.success).length || 0;
      toast({ title: `Pinged ${successCount} search engines` });
    },
    onError: () => {
      toast({ title: "Failed to ping search engines", variant: "destructive" });
    },
  });

  const refreshSocialProofMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/seo/social-proof/refresh");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Social proof metrics updated" });
    },
  });

  const runHealthCheckMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/seo/health-check");
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: `SEO Health Score: ${data.score}/100` });
    },
  });

  const generateBlogMutation = useMutation({
    mutationFn: async (data: { topic: string; keywords: string[] }) => {
      const res = await apiRequest("POST", "/api/seo/blog/generate", data);
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: "Blog post generated", description: data.title });
      queryClient.invalidateQueries({ queryKey: ["/api/seo/blog"] });
      setBlogTopic("");
      setBlogKeywords("");
      setIsGeneratingBlog(false);
    },
    onError: () => {
      toast({ title: "Failed to generate blog post", variant: "destructive" });
      setIsGeneratingBlog(false);
    },
  });

  const addKeywordMutation = useMutation({
    mutationFn: async (keyword: string) => {
      const res = await apiRequest("POST", "/api/seo/keywords", { keyword });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Keyword added for tracking" });
      queryClient.invalidateQueries({ queryKey: ["/api/seo/keywords"] });
      setNewKeyword("");
    },
  });

  const toggleCronJobMutation = useMutation({
    mutationFn: async ({ id, isEnabled }: { id: string; isEnabled: boolean }) => {
      const res = await apiRequest("PUT", `/api/seo/cron/jobs/${id}`, { isEnabled });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/seo/cron/jobs"] });
    },
  });

  const [runningJobId, setRunningJobId] = useState<string | null>(null);

  const runCronJobMutation = useMutation({
    mutationFn: async (id: string) => {
      setRunningJobId(id);
      const res = await apiRequest("POST", `/api/seo/cron/jobs/${id}/run`);
      return res.json();
    },
    onSuccess: (data: { success: boolean; message: string }) => {
      toast({
        title: data.success ? "Job completed" : "Job failed",
        description: data.message,
        variant: data.success ? undefined : "destructive",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/seo/cron/jobs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/seo/logs"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Job failed",
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => setRunningJobId(null),
  });

  // Generate Weekly SEO Report
  const generateWeeklyReport = async () => {
    setIsGeneratingReport(true);
    try {
      const res = await apiRequest("GET", "/api/seo/report/weekly");
      const data = await res.json();

      // Create downloadable report
      const reportContent = `
SEO WEEKLY REPORT
Generated: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
================================================================================

SUMMARY
-------
• Published Blog Posts: ${data.blogStats?.published || 0}
• Draft Posts: ${data.blogStats?.drafts || 0}
• Total Page Views (This Week): ${data.pageViews || 0}
• Programmatic SEO Pages: ${data.programmaticPages || 0}

BLOG PERFORMANCE
----------------
${data.blogs?.map((blog: any) => `• ${blog.title}
  - Views: ${blog.viewCount || 0}
  - Published: ${blog.publishedAt ? new Date(blog.publishedAt).toLocaleDateString() : 'N/A'}
  - Category: ${blog.category || 'Uncategorized'}
`).join('\n') || 'No blog posts this week.'}

KEYWORD RANKINGS
----------------
${data.keywords?.map((kw: any) => {
  const change = kw.previousPosition && kw.currentPosition
    ? kw.previousPosition - kw.currentPosition
    : 0;
  const changeStr = change > 0 ? `↑${change}` : change < 0 ? `↓${Math.abs(change)}` : '→';
  return `• ${kw.keyword}: Position ${kw.currentPosition || 'N/A'} (${changeStr})`;
}).join('\n') || 'No keywords tracked.'}

AUTOMATION STATUS
-----------------
• Blog Generation: ${data.cronStatus?.blogGeneration || 'Saturdays at 5:00 AM'}
• Sitemap Updates: ${data.cronStatus?.sitemapUpdates || 'Every 6 hours'}
• Keyword Tracking: ${data.cronStatus?.keywordTracking || 'Daily at 2:00 AM'}

BACKLINKS
---------
• Active Backlinks: ${data.backlinks?.active || 0}
• New This Week: ${data.backlinks?.newThisWeek || 0}
• Broken Links: ${data.backlinks?.broken || 0}

RECOMMENDATIONS
---------------
${data.recommendations?.map((rec: string, i: number) => `${i + 1}. ${rec}`).join('\n') || '• All SEO metrics look healthy!'}

================================================================================
Report generated by VoicePro SEO Automation
      `.trim();

      // Download as text file
      const blob = new Blob([reportContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `seo-weekly-report-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Report Generated",
        description: "Weekly SEO report has been downloaded",
      });
    } catch (error) {
      toast({
        title: "Failed to generate report",
        description: "An error occurred while generating the report",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "error":
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case "running":
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPositionChange = (current: number | null, previous: number | null) => {
    if (current === null || previous === null) return null;
    const diff = previous - current;
    if (diff > 0) return { direction: "up", value: diff };
    if (diff < 0) return { direction: "down", value: Math.abs(diff) };
    return { direction: "same", value: 0 };
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SEO Dashboard</h1>
          <p className="text-muted-foreground">
            Manage organic promotion, content, and search engine optimization
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={generateWeeklyReport}
            disabled={isGeneratingReport}
          >
            {isGeneratingReport ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4 mr-2" />
            )}
            Weekly Report
          </Button>
          <Button
            variant="outline"
            onClick={() => refreshSitemapMutation.mutate()}
            disabled={refreshSitemapMutation.isPending}
          >
            {refreshSitemapMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh Sitemap
          </Button>
          <Button
            onClick={() => pingSearchEnginesMutation.mutate()}
            disabled={pingSearchEnginesMutation.isPending}
          >
            {pingSearchEnginesMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Globe className="h-4 w-4 mr-2" />
            )}
            Ping Search Engines
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="automation">Automation</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="keywords">Keywords</TabsTrigger>
          <TabsTrigger value="cron">Cron Jobs</TabsTrigger>
          <TabsTrigger value="logs">Activity Logs</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Blog Posts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? "-" : dashboard?.stats?.publishedBlogPosts || 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  SEO Pages
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? "-" : dashboard?.stats?.publishedPages || 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Templates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? "-" : dashboard?.stats?.publicTemplates || 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Widgets
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? "-" : dashboard?.stats?.activeWidgets || 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Backlinks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? "-" : dashboard?.stats?.activeBacklinks || 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Keywords
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardLoading ? "-" : dashboard?.stats?.trackedKeywords || 0}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common SEO tasks you can run manually</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button
                  variant="outline"
                  className="h-20 flex-col"
                  onClick={() => runHealthCheckMutation.mutate()}
                  disabled={runHealthCheckMutation.isPending}
                >
                  {runHealthCheckMutation.isPending ? (
                    <Loader2 className="h-6 w-6 mb-2 animate-spin" />
                  ) : (
                    <BarChart3 className="h-6 w-6 mb-2" />
                  )}
                  <span>Run Health Check</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex-col"
                  onClick={() => refreshSocialProofMutation.mutate()}
                  disabled={refreshSocialProofMutation.isPending}
                >
                  {refreshSocialProofMutation.isPending ? (
                    <Loader2 className="h-6 w-6 mb-2 animate-spin" />
                  ) : (
                    <TrendingUp className="h-6 w-6 mb-2" />
                  )}
                  <span>Update Social Proof</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex-col"
                  onClick={() => window.open("/sitemap.xml", "_blank")}
                >
                  <FileText className="h-6 w-6 mb-2" />
                  <span>View Sitemap</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex-col"
                  onClick={() => window.open("/robots.txt", "_blank")}
                >
                  <Code className="h-6 w-6 mb-2" />
                  <span>View Robots.txt</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboard?.recentLogs?.slice(0, 5).map((log) => (
                  <div key={log.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(log.status)}
                      <div>
                        <p className="font-medium">{log.action}</p>
                        <p className="text-sm text-muted-foreground">{log.message}</p>
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {formatRelativeTime(log.createdAt)}
                    </span>
                  </div>
                ))}
                {(!dashboard?.recentLogs || dashboard.recentLogs.length === 0) && (
                  <p className="text-muted-foreground text-center py-4">No recent activity</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automation Tab */}
        <TabsContent value="automation" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>SEO Automation Status</CardTitle>
              <CardDescription>
                All SEO tasks run automatically on a schedule. Use these controls to trigger tasks manually.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <AutomationCard
                  title="Run All Tasks"
                  description="Execute all automated SEO tasks immediately"
                  endpoint="/api/seo/automation/run-all"
                  icon={<Play className="h-5 w-5" />}
                />
                <AutomationCard
                  title="Auto-Publish Posts"
                  description="Publish any scheduled blog posts that are due"
                  endpoint="/api/seo/automation/auto-publish"
                  icon={<FileText className="h-5 w-5" />}
                />
                <AutomationCard
                  title="Generate Blog Post"
                  description="Auto-generate a new blog post from trending topics"
                  endpoint="/api/seo/automation/auto-blog"
                  icon={<Sparkles className="h-5 w-5" />}
                />
                <AutomationCard
                  title="Send Review Emails"
                  description="Send pending review request emails to qualified users"
                  endpoint="/api/seo/automation/review-emails"
                  icon={<Share2 className="h-5 w-5" />}
                />
                <AutomationCard
                  title="Generate Review Requests"
                  description="Find and create review requests for qualified users"
                  endpoint="/api/seo/automation/generate-reviews"
                  icon={<Plus className="h-5 w-5" />}
                />
                <AutomationCard
                  title="Check Backlinks"
                  description="Check all tracked backlinks for broken links"
                  endpoint="/api/seo/automation/check-backlinks"
                  icon={<Link2 className="h-5 w-5" />}
                />
                <AutomationCard
                  title="Internal Links"
                  description="Analyze content and suggest internal linking opportunities"
                  endpoint="/api/seo/automation/internal-links"
                  icon={<Link2 className="h-5 w-5" />}
                />
                <AutomationCard
                  title="Share to LinkedIn"
                  description="Share the latest blog post to LinkedIn as a newsletter"
                  endpoint="/api/seo/linkedin/share-latest"
                  icon={<Linkedin className="h-5 w-5 text-[#0A66C2]" />}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Automation Schedule</CardTitle>
              <CardDescription>
                Tasks run automatically at these times (server timezone)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Driven by seo_cron_jobs rather than hardcoded: this list used
                  to claim schedules the scheduler was not actually running. */}
              <div className="space-y-3">
                {cronJobs?.map((job) => (
                  <ScheduleItem
                    key={job.id}
                    task={job.jobName || job.jobType}
                    schedule={job.isEnabled ? describeCron(job.cronExpression) : "Disabled"}
                  />
                ))}
                {(!cronJobs || cronJobs.length === 0) && (
                  <p className="text-sm text-muted-foreground py-4">
                    No scheduled tasks configured.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Content Tab */}
        <TabsContent value="content" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Blog Posts</CardTitle>
                <CardDescription>Manage your blog content</CardDescription>
              </div>
              <Dialog open={isGeneratingBlog} onOpenChange={setIsGeneratingBlog}>
                <DialogTrigger asChild>
                  <Button>
                    <Wand2 className="h-4 w-4 mr-2" />
                    Generate with AI
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Generate Blog Post with AI</DialogTitle>
                    <DialogDescription>
                      Enter a topic and keywords to generate an SEO-optimized blog post
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Topic</Label>
                      <Input
                        placeholder="e.g., How AI Voice Agents Transform Customer Service"
                        value={blogTopic}
                        onChange={(e) => setBlogTopic(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Target Keywords (comma-separated)</Label>
                      <Input
                        placeholder="e.g., AI voice agents, customer service, automation"
                        value={blogKeywords}
                        onChange={(e) => setBlogKeywords(e.target.value)}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={() => {
                        generateBlogMutation.mutate({
                          topic: blogTopic,
                          keywords: blogKeywords.split(",").map(k => k.trim()).filter(Boolean),
                        });
                      }}
                      disabled={generateBlogMutation.isPending || !blogTopic || !blogKeywords}
                    >
                      {generateBlogMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4 mr-2" />
                      )}
                      Generate
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Views</TableHead>
                    <TableHead>Published</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blogPosts?.map((post) => (
                    <TableRow key={post.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {post.title}
                          {post.aiGenerated && (
                            <Badge variant="secondary" className="text-xs">
                              <Sparkles className="h-3 w-3 mr-1" />
                              AI
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={post.status === "published" ? "default" : "secondary"}>
                          {post.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{post.category || "-"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Eye className="h-4 w-4 text-muted-foreground" />
                          {post.viewCount}
                        </div>
                      </TableCell>
                      <TableCell>
                        {post.publishedAt
                          ? formatRelativeTime(post.publishedAt)
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm">
                            Edit
                          </Button>
                          {post.status === "published" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const res = await apiRequest("POST", `/api/seo/linkedin/share/${post.id}`);
                                  const data = await res.json();
                                  if (data.success) {
                                    toast({
                                      title: "Shared to LinkedIn!",
                                      description: data.postUrl ? (
                                        <a href={data.postUrl} target="_blank" rel="noopener noreferrer" className="underline">
                                          View post
                                        </a>
                                      ) : "Post shared successfully",
                                    });
                                  } else {
                                    toast({
                                      title: "Share failed",
                                      description: data.error || "Failed to share to LinkedIn",
                                      variant: "destructive",
                                    });
                                  }
                                } catch (error) {
                                  toast({
                                    title: "Share failed",
                                    description: "An error occurred while sharing to LinkedIn",
                                    variant: "destructive",
                                  });
                                }
                              }}
                              title="Share to LinkedIn"
                            >
                              <Linkedin className="h-4 w-4 text-[#0A66C2]" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/blog/${post.slug}`, "_blank")}
                            title="View post"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!blogPosts || blogPosts.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No blog posts yet. Generate one with AI to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Keywords Tab */}
        <TabsContent value="keywords" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Keyword Rankings</CardTitle>
                <CardDescription>Track your keyword positions in search results</CardDescription>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add keyword..."
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  className="w-64"
                />
                <Button
                  onClick={() => addKeywordMutation.mutate(newKeyword)}
                  disabled={addKeywordMutation.isPending || !newKeyword}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Keyword</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead>Target URL</TableHead>
                    <TableHead>Last Checked</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keywords?.map((kw) => {
                    const change = getPositionChange(kw.currentPosition, kw.previousPosition);
                    return (
                      <TableRow key={kw.id}>
                        <TableCell className="font-medium">{kw.keyword}</TableCell>
                        <TableCell>
                          {kw.currentPosition ? (
                            <Badge variant={kw.currentPosition <= 10 ? "default" : "secondary"}>
                              #{kw.currentPosition}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">Not ranked</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {change ? (
                            <div className="flex items-center gap-1">
                              {change.direction === "up" && (
                                <>
                                  <TrendingUp className="h-4 w-4 text-green-500" />
                                  <span className="text-green-500">+{change.value}</span>
                                </>
                              )}
                              {change.direction === "down" && (
                                <>
                                  <TrendingDown className="h-4 w-4 text-red-500" />
                                  <span className="text-red-500">-{change.value}</span>
                                </>
                              )}
                              {change.direction === "same" && (
                                <Minus className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {kw.targetUrl ? (
                            <a
                              href={kw.targetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline flex items-center gap-1"
                            >
                              {kw.targetUrl.slice(0, 30)}...
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {kw.lastCheckedAt
                            ? formatRelativeTime(kw.lastCheckedAt)
                            : "Never"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!keywords || keywords.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No keywords tracked yet. Add keywords to monitor your search rankings.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cron Jobs Tab */}
        <TabsContent value="cron" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Scheduled Tasks</CardTitle>
              <CardDescription>
                Automated SEO tasks that run on a schedule
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Last Run</TableHead>
                    <TableHead>Next Run</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Enabled</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cronJobs?.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell className="font-medium">
                        {job.jobName || job.jobType}
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {job.cronExpression}
                        </code>
                        <div className="text-xs text-muted-foreground mt-1">
                          {describeCron(job.cronExpression)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {job.lastRunAt
                          ? formatRelativeTime(job.lastRunAt)
                          : "Never"}
                      </TableCell>
                      <TableCell>
                        {!job.isEnabled ? (
                          <span className="text-muted-foreground">Disabled</span>
                        ) : job.nextRunAt ? (
                          formatRelativeTime(job.nextRunAt)
                        ) : (
                          <span className="text-destructive">Not scheduled</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(job.lastRunStatus || "pending")}
                          <span className="capitalize">{job.lastRunStatus || "Pending"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={job.isEnabled}
                          onCheckedChange={(checked) =>
                            toggleCronJobMutation.mutate({ id: job.id, isEnabled: checked })
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={runningJobId !== null}
                          onClick={() => runCronJobMutation.mutate(job.id)}
                          data-testid={`run-cron-${job.jobType}`}
                        >
                          {runningJobId === job.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Play className="h-3 w-3" />
                          )}
                          <span className="ml-2">Run Now</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!cronJobs || cronJobs.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No scheduled tasks found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Activity Logs</CardTitle>
              <CardDescription>Recent SEO automation activity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboard?.recentLogs?.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-start justify-between border-b pb-4 last:border-0"
                  >
                    <div className="flex items-start gap-3">
                      {getStatusIcon(log.status)}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{log.action}</p>
                          <Badge variant="outline" className="text-xs">
                            {log.category}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{log.message}</p>
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatRelativeTime(log.createdAt)}
                    </span>
                  </div>
                ))}
                {(!dashboard?.recentLogs || dashboard.recentLogs.length === 0) && (
                  <p className="text-muted-foreground text-center py-8">No activity logs yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
